/**
 * LiveKit data-channel sync for ephemeral emoji reactions.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useMaybeRoomContext } from "@livekit/components-react";
import { RoomEvent } from "livekit-client";
import {
  LIVE_REACTION_TYPE,
  type FloatingReaction,
  type LiveReactionMessage,
} from "./liveReactionTypes";

const REACTION_TTL_MS = 3200;
const MAX_VISIBLE = 24;
const MIN_SEND_GAP_MS = 800;

function encode(msg: object): Uint8Array {
  return new TextEncoder().encode(JSON.stringify(msg));
}

function decode(data: Uint8Array): Record<string, unknown> | null {
  try {
    return JSON.parse(new TextDecoder().decode(data)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function newId(): string {
  return `rx-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function toFloating(msg: LiveReactionMessage): FloatingReaction {
  return {
    reaction_id: msg.reaction_id,
    emoji: msg.emoji,
    from_name: msg.from_name,
    at: msg.at,
    x: 12 + Math.random() * 76,
  };
}

export function useLiveReactionSync(options: {
  enabled?: boolean;
  fromId: string;
  fromName: string;
}) {
  const room = useMaybeRoomContext();
  const [floating, setFloating] = useState<FloatingReaction[]>([]);
  const lastSendAtRef = useRef(0);

  const pushLocal = useCallback((item: FloatingReaction) => {
    setFloating((prev) => {
      const next = [...prev, item];
      return next.length > MAX_VISIBLE ? next.slice(-MAX_VISIBLE) : next;
    });
    window.setTimeout(() => {
      setFloating((prev) =>
        prev.filter((r) => r.reaction_id !== item.reaction_id),
      );
    }, REACTION_TTL_MS);
  }, []);

  useEffect(() => {
    if (!options.enabled || !room) return;

    const onData = (payload: Uint8Array) => {
      const raw = decode(payload);
      if (!raw || raw.v !== 1 || raw.type !== LIVE_REACTION_TYPE) return;
      const emoji = String(raw.emoji || "").trim();
      if (!emoji) return;
      const msg: LiveReactionMessage = {
        type: LIVE_REACTION_TYPE,
        v: 1,
        reaction_id: String(raw.reaction_id || newId()),
        emoji,
        from_id: String(raw.from_id || ""),
        from_name: String(raw.from_name || ""),
        at: Number(raw.at || Date.now()),
      };
      if (msg.from_id && msg.from_id === options.fromId) return;
      pushLocal(toFloating(msg));
    };

    room.on(RoomEvent.DataReceived, onData);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
    };
  }, [options.enabled, options.fromId, room, pushLocal]);

  const sendReaction = useCallback(
    async (emoji: string) => {
      const trimmed = String(emoji || "").trim();
      if (!trimmed || !room?.localParticipant) return;
      const now = Date.now();
      if (now - lastSendAtRef.current < MIN_SEND_GAP_MS) return;
      lastSendAtRef.current = now;
      const msg: LiveReactionMessage = {
        type: LIVE_REACTION_TYPE,
        v: 1,
        reaction_id: newId(),
        emoji: trimmed,
        from_id: options.fromId,
        from_name: options.fromName,
        at: now,
      };
      pushLocal(toFloating(msg));
      try {
        void room.localParticipant.publishData(encode(msg), {
          reliable: false,
        });
      } catch (err) {
        console.error("publish reaction failed", err);
      }
    },
    [options.fromId, options.fromName, pushLocal, room],
  );

  return { floating, sendReaction };
}
